import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import SegmentedTabs from "@/components/base/SegmentedTabs";
import Tag from "@/components/base/Tag";
import { cultureTopics } from "@/mocks/culture";
import { getContentById, typeLabel } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function CulturePage() {
  usePageMeta({
    title: "动漫文化 - 星空动漫",
    description:
      "星空动漫动漫文化专题页，从动画发展历史、名家介绍到周边与同人文化，用专题合集的方式带你了解动漫背后的文化脉络。",
    keywords: "动漫文化,动画历史,名家介绍,声优文化,周边文化,同人文化,星空动漫",
  });

  const [group, setGroup] = useState("all");

  const groups = useMemo(() => {
    const set = new Set(cultureTopics.map((topic) => topic.group));
    return Array.from(set);
  }, []);

  const filtered = useMemo(() => {
    if (group === "all") return cultureTopics;
    return cultureTopics.filter((topic) => topic.group === group);
  }, [group]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof cultureTopics>();
    filtered.forEach((topic) => {
      const list = map.get(topic.group) || [];
      list.push(topic);
      map.set(topic.group, list);
    });
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <SiteLayout>
      <div className="mx-auto max-w-page px-4 py-8 md:px-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫文化" }]} />

        <section className="mt-4 overflow-hidden rounded-lg border border-background-200">
          <div className="relative h-[240px] w-full md:h-[320px]">
            <img
              src="https://readdy.ai/api/search-image?query=anime%20themed%20illustration%20of%20a%20traditional%20eastern%20torii%20gate%20and%20glowing%20lanterns%20under%20a%20vast%20starry%20night%20sky%2C%20deep%20blue%20and%20amber%20palette%2C%20cinematic%20wide%20composition&width=1600&height=700&seq=culture-hero&orientation=landscape"
              alt="动漫文化专题"
              title="动漫文化专题｜星空动漫"
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/25" />
            <div className="absolute inset-0 flex items-end">
              <div className="w-full px-5 pb-8 md:px-10 md:pb-10">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-accent-950">
                  <i className="ri-book-2-line" />
                  文化专题
                </span>
                <h1 className="mt-3 text-2xl font-bold text-background-50 md:text-4xl">
                  动漫文化专题
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
                  动漫不只是作品，更是一整套文化。这里从发展历史、创作者、声优到周边与同人文化，带你走进热闹又细腻的二次元世界。
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="mt-6">
          <SegmentedTabs
            value={group}
            onChange={setGroup}
            segments={[
              { value: "all", label: "全部专题" },
              ...groups.map((item) => ({ value: item, label: item })),
            ]}
          />
        </div>

        <div className="mt-8 flex flex-col gap-10">
          {grouped.map(([groupName, topics]) => (
            <section key={groupName}>
              <div className="mb-5 flex items-center gap-3">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                <h2 className="text-xl font-bold text-foreground-950">{groupName}</h2>
                <span className="text-sm text-foreground-400">{topics.length} 个专题</span>
              </div>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {topics.map((topic) => {
                  const articles = topic.articles
                    .map((id) => getContentById(id))
                    .filter((item): item is NonNullable<typeof item> => Boolean(item));
                  const primary = articles[0];
                  return (
                    <article
                      key={topic.id}
                      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                    >
                      <div className="aspect-[16/9] w-full overflow-hidden bg-background-200">
                        <img
                          src={topic.cover}
                          alt={topic.title}
                          title={`${topic.title}｜星空动漫`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <div className="mb-2 flex items-center gap-2">
                          <Tag variant="accent">{topic.group}</Tag>
                          <Tag variant="secondary">专题合集</Tag>
                        </div>
                        <h3 className="text-lg font-semibold text-foreground-950">{topic.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                          {topic.summary}
                        </p>
                        <p className="mt-3 rounded-md bg-accent-50 px-3 py-2 text-xs leading-relaxed text-accent-800">
                          {topic.reason}
                        </p>

                        <div className="mt-4 border-t border-background-200 pt-4">
                          <p className="mb-2 text-xs font-medium text-foreground-500">专题相关文章</p>
                          <div className="flex flex-col gap-2">
                            {articles.map((item) => (
                              <Link
                                key={item.id}
                                to={`/detail/${item.id}`}
                                className="group/item flex cursor-pointer items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                              >
                                <i className="ri-arrow-right-s-line text-base text-accent-500" />
                                <span className="line-clamp-1">{item.title}</span>
                                <span className="ml-auto shrink-0 text-xs text-foreground-400">
                                  {typeLabel(item.type)}
                                </span>
                              </Link>
                            ))}
                          </div>
                        </div>

                        {primary ? (
                          <Link
                            to={`/detail/${primary.id}`}
                            className="mt-4 inline-flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                          >
                            进入专题
                            <i className="ri-arrow-right-line" />
                          </Link>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="text-xl font-bold text-foreground-950">相关专题入口</h2>
          <p className="mt-2 text-sm text-foreground-600">按分组继续浏览其他文化专题。</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {groups.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setGroup(item)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-folder-line text-accent-500" />
                {item}
              </button>
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}